import React, {useMemo, useState} from 'react';
import {
    BarChart, Bar, LineChart, Line,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import Moment from 'moment';
import 'moment-timezone';
import NumberFormat from 'react-number-format';

const COLORS = {
    sent: '#3b82f6',      // blue
    used: '#10b981',      // green
    rate: '#8b5cf6',      // purple
};

/**
 * VoucherAnalytics component
 *
 * Displays voucher/campaign analytics for campaigns with "poklon" or "gratis" type.
 * Uses pre-aggregated data from Firestore.
 *
 * @param {Object} voucherReport - Pre-aggregated voucher report from Firestore
 */
export const VoucherAnalytics = ({voucherReport}) => {
    const [sortField, setSortField] = useState('createdAt');
    const [sortDirection, setSortDirection] = useState('desc');
    const [filterType, setFilterType] = useState('all');
    const [timePeriod, setTimePeriod] = useState('last12'); // 'allTime' or 'last12'

    const hasData = voucherReport && voucherReport.summary;

    const analytics = useMemo(() => {
        if (!hasData) {
            return {
                summary: {
                    totalVoucherCampaigns: 0,
                    totalSent: 0,
                    totalUsed: 0,
                    overallUsageRate: 0
                },
                aggregatedByType: [],
                monthlyTrend: [],
                campaigns: []
            };
        }

        // Select data based on time period
        const isAllTime = timePeriod === 'allTime';

        const summary = isAllTime
            ? (voucherReport.summaryAllTime || voucherReport.summary || {})
            : (voucherReport.summaryLast12Months || voucherReport.summary || {});

        const aggregatedByType = isAllTime
            ? (voucherReport.aggregatedByTypeAllTime || voucherReport.aggregatedByType || [])
            : (voucherReport.aggregatedByTypeLast12Months || voucherReport.aggregatedByType || []);

        const rawMonthlyTrend = isAllTime
            ? (voucherReport.monthlyTrendAllTime || voucherReport.monthlyTrend || [])
            : (voucherReport.monthlyTrendLast12Months || voucherReport.monthlyTrend || []);

        const campaigns = isAllTime
            ? (voucherReport.campaignsAllTime || voucherReport.campaigns || [])
            : (voucherReport.campaignsLast12Months || voucherReport.campaigns || []);

        // Format monthly trend with labels
        const monthlyTrend = rawMonthlyTrend.map(item => ({
            ...item,
            monthLabel: Moment(item.month, 'YYYY-MM').format('MMM YY')
        }));

        return {
            summary,
            aggregatedByType,
            monthlyTrend,
            campaigns
        };
    }, [voucherReport, hasData, timePeriod]);

    // Get unique campaign types for filter
    const campaignTypes = useMemo(() => {
        const types = new Set(analytics.campaigns.map(c => c.campaignType));
        return Array.from(types).sort();
    }, [analytics.campaigns]);

    // Filter and sort campaigns
    const filteredCampaigns = useMemo(() => {
        let filtered = [...analytics.campaigns];

        // Apply type filter
        if (filterType !== 'all') {
            filtered = filtered.filter(c => c.campaignType === filterType);
        }

        // Apply sorting
        filtered.sort((a, b) => {
            let aVal = a[sortField];
            let bVal = b[sortField];

            // Handle null/undefined
            if (aVal === null || aVal === undefined) aVal = sortDirection === 'asc' ? Infinity : -Infinity;
            if (bVal === null || bVal === undefined) bVal = sortDirection === 'asc' ? Infinity : -Infinity;

            // Handle dates
            if (sortField === 'createdAt') {
                aVal = aVal ? new Date(aVal).getTime() : 0;
                bVal = bVal ? new Date(bVal).getTime() : 0;
            }

            if (sortDirection === 'asc') {
                return aVal > bVal ? 1 : -1;
            } else {
                return aVal < bVal ? 1 : -1;
            }
        });

        return filtered;
    }, [analytics.campaigns, filterType, sortField, sortDirection]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
    };

    const SortIcon = ({field}) => {
        if (sortField !== field) return <span className="text-gray-300 ml-1">↕</span>;
        return <span className="text-blue-500 ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
    };

    if (!hasData) {
        return (
            <div className="space-y-6">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                    <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Voucher Analytics</h3>
                    <p className="text-gray-600">
                        Ovaj izveštaj trenutno nije dostupan za vašu kompaniju.
                    </p>
                    <p className="text-gray-500 text-sm mt-2">
                        Kontaktirajte <span className="font-medium">Digitalnu Karticu</span> za aktivaciju ove funkcionalnosti.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Data Source Indicator & Time Period Toggle */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-blue-600 font-medium">Agregirani podaci</span>
                        <span className="text-blue-500 text-sm">
                            (ažurirano: {voucherReport.generatedAt ?
                                Moment(voucherReport.generatedAt.toDate ? voucherReport.generatedAt.toDate() : voucherReport.generatedAt)
                                    .format('DD.MM.YYYY HH:mm') : 'N/A'})
                        </span>
                    </div>

                    {/* Time Period Toggle */}
                    <div className="flex items-center bg-white rounded-lg p-1 shadow-sm border border-blue-200">
                        <button
                            onClick={() => setTimePeriod('last12')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                                timePeriod === 'last12'
                                    ? 'bg-blue-600 text-white'
                                    : 'text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                            Poslednjih 12 meseci
                        </button>
                        <button
                            onClick={() => setTimePeriod('allTime')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                                timePeriod === 'allTime'
                                    ? 'bg-blue-600 text-white'
                                    : 'text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                            Svo vreme
                        </button>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-lg shadow border">
                    <div className="text-sm text-gray-500">Ukupno kampanja</div>
                    <div className="text-2xl font-bold text-gray-900">
                        <NumberFormat value={analytics.summary.totalVoucherCampaigns} displayType="text" thousandSeparator="." decimalSeparator="," />
                    </div>
                    <div className="text-xs text-gray-400">
                        {timePeriod === 'allTime' ? 'svo vreme' : 'poslednjih 12 meseci'}
                    </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow border">
                    <div className="text-sm text-gray-500">Ukupno poslato</div>
                    <div className="text-2xl font-bold text-blue-600">
                        <NumberFormat value={analytics.summary.totalSent} displayType="text" thousandSeparator="." decimalSeparator="," />
                    </div>
                    <div className="text-xs text-gray-400">vaučera</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow border">
                    <div className="text-sm text-gray-500">Ukupno iskorišćeno</div>
                    <div className="text-2xl font-bold text-green-600">
                        <NumberFormat value={analytics.summary.totalUsed} displayType="text" thousandSeparator="." decimalSeparator="," />
                    </div>
                    <div className="text-xs text-gray-400">vaučera</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow border">
                    <div className="text-sm text-gray-500">Stopa iskorišćenosti</div>
                    <div className="text-2xl font-bold text-purple-600">
                        {analytics.summary.overallUsageRate?.toFixed(2)}%
                    </div>
                    <div className="text-xs text-gray-400">iskorišćeno / poslato</div>
                </div>
            </div>

            {/* Aggregated by Type */}
            {analytics.aggregatedByType.length > 0 && (
                <div className="bg-white p-4 rounded-lg shadow border">
                    <h3 className="text-lg font-semibold mb-4">
                        Agregirano po tipu kampanje
                        <span className="text-sm font-normal text-gray-500 ml-2">
                            ({timePeriod === 'allTime' ? 'svo vreme' : 'poslednjih 12 meseci'})
                        </span>
                    </h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tip kampanje</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Broj kampanja</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Poslato</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Iskorišćeno</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Stopa</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {analytics.aggregatedByType.map((item, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.campaignType}</td>
                                        <td className="px-4 py-3 text-sm text-gray-500 text-right">
                                            <NumberFormat value={item.campaignCount} displayType="text" thousandSeparator="." decimalSeparator="," />
                                        </td>
                                        <td className="px-4 py-3 text-sm text-blue-600 text-right">
                                            <NumberFormat value={item.totalSent} displayType="text" thousandSeparator="." decimalSeparator="," />
                                        </td>
                                        <td className="px-4 py-3 text-sm text-green-600 text-right">
                                            <NumberFormat value={item.totalUsed} displayType="text" thousandSeparator="." decimalSeparator="," />
                                        </td>
                                        <td className="px-4 py-3 text-sm text-purple-600 text-right font-medium">
                                            {item.usageRate?.toFixed(2)}%
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Monthly Trend Chart */}
            {analytics.monthlyTrend.length > 0 && (
                <div className="bg-white p-4 rounded-lg shadow border">
                    <h3 className="text-lg font-semibold mb-4">
                        Mesečni trend
                        <span className="text-sm font-normal text-gray-500 ml-2">
                            ({timePeriod === 'allTime' ? 'svo vreme' : 'poslednjih 12 meseci'})
                        </span>
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={analytics.monthlyTrend}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="monthLabel" />
                            <YAxis yAxisId="left" orientation="left" />
                            <YAxis yAxisId="right" orientation="right" unit="%" />
                            <Tooltip
                                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                                formatter={(value, name) => {
                                    const labels = { totalSent: 'Poslato', totalUsed: 'Iskorišćeno', usageRate: 'Stopa (%)' };
                                    const formattedValue = name === 'usageRate' ? `${value.toFixed(2)}%` : value.toLocaleString('de-DE');
                                    return [formattedValue, labels[name] || name];
                                }}
                            />
                            <Legend
                                formatter={(value) => {
                                    const labels = { totalSent: 'Poslato', totalUsed: 'Iskorišćeno', usageRate: 'Stopa iskorišćenosti' };
                                    return labels[value] || value;
                                }}
                            />
                            <Bar yAxisId="left" dataKey="totalSent" name="totalSent" fill={COLORS.sent} />
                            <Bar yAxisId="left" dataKey="totalUsed" name="totalUsed" fill={COLORS.used} />
                            <Line yAxisId="right" type="monotone" dataKey="usageRate" name="usageRate" stroke={COLORS.rate} strokeWidth={2} dot={{ r: 4 }} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Individual Campaigns Table */}
            <div className="bg-white p-4 rounded-lg shadow border">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                    <h3 className="text-lg font-semibold">
                        Pojedinačne kampanje ({filteredCampaigns.length})
                        <span className="text-sm font-normal text-gray-500 ml-2">
                            ({timePeriod === 'allTime' ? 'svo vreme' : 'poslednjih 12 meseci'})
                        </span>
                    </h3>
                    <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-500">Filter po tipu:</label>
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                        >
                            <option value="all">Svi tipovi</option>
                            {campaignTypes.map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="overflow-x-auto max-h-96">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0">
                            <tr>
                                <th
                                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('name')}
                                >
                                    Naziv <SortIcon field="name" />
                                </th>
                                <th
                                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('campaignType')}
                                >
                                    Tip <SortIcon field="campaignType" />
                                </th>
                                <th
                                    className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('sentCount')}
                                >
                                    Poslato <SortIcon field="sentCount" />
                                </th>
                                <th
                                    className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('usedCount')}
                                >
                                    Iskorišćeno <SortIcon field="usedCount" />
                                </th>
                                <th
                                    className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('usageRate')}
                                >
                                    Stopa <SortIcon field="usageRate" />
                                </th>
                                <th
                                    className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('createdAt')}
                                >
                                    Datum <SortIcon field="createdAt" />
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredCampaigns.map((campaign) => (
                                <tr key={campaign.id} className="hover:bg-gray-50">
                                    <td className="px-3 py-2 text-sm text-gray-900 max-w-xs truncate" title={campaign.name}>
                                        {campaign.name}
                                    </td>
                                    <td className="px-3 py-2 text-sm text-gray-500">{campaign.campaignType}</td>
                                    <td className="px-3 py-2 text-sm text-blue-600 text-right">
                                        <NumberFormat value={campaign.sentCount} displayType="text" thousandSeparator="." decimalSeparator="," />
                                    </td>
                                    <td className="px-3 py-2 text-sm text-green-600 text-right">
                                        <NumberFormat value={campaign.usedCount} displayType="text" thousandSeparator="." decimalSeparator="," />
                                    </td>
                                    <td className="px-3 py-2 text-sm text-right">
                                        <span className={`font-medium ${campaign.usageRate >= 50 ? 'text-green-600' : campaign.usageRate >= 20 ? 'text-yellow-600' : 'text-red-500'}`}>
                                            {campaign.usageRate?.toFixed(1)}%
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-sm text-gray-500 text-right">
                                        {campaign.createdAt ? Moment(campaign.createdAt).format('DD.MM.YYYY') : '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
